/**
 * The card you just played, leaving your hand.
 *
 * **Yours alone, and a different animation from the table's.** The table shows
 * a face-down card travelling between a seat and a pile, which is what the room
 * needs: who acted, and roughly what happened. This is the other half - the
 * card you actually chose, face up, going where you sent it - and only you see
 * it, because only you tapped it.
 *
 * Positioned in viewport pixels rather than as a percentage of anything. It
 * crosses from the hand into the table behind it, which are different
 * containers with different sizes, and the only coordinate space both share is
 * the screen.
 *
 * ## And the card you turned, which is the same animation backwards
 *
 * A patience has no hand. You turn the top of the stock onto the waste, and
 * both ends are piles on the board - so there is no card to tap, no `args[0]`
 * naming one, and nothing in the hand to fly from. `useTurned` below drives
 * that case off the packet coming back instead of off the tap, because the
 * card's identity is the one thing you do not know until the server answers.
 *
 * It lives here rather than with the table's flight because the ring is
 * `display: none` on a board game - a flight parented to it has no size and
 * nothing to divide by - and this layer is a sibling of the ring.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameLayoutWire } from '../net/session.js';
import type { ZoneView } from '../net/match.js';
import { SEAT_END } from '@cards/toolkit';
import { Card, CardBack } from '../render/Card.js';
import { landedCard } from '../render/arrival.js';
import { useCounterEvent } from '../render/use-counter-event.js';

export interface Throw {
  key: number;
  card: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  /** Fly a back rather than the face. A card off a stock is face down. */
  faceDown?: boolean | undefined;
  /**
   * Lands, rather than dissolving into the pile it reaches.
   *
   * A card played from your hand fades out as it arrives, because the real one
   * is already on the discard underneath it and two would be one too many. A
   * turned card has nothing underneath - the pile is holding its place empty
   * until this lands - so fading it out plays the last third of the journey to
   * an empty square and then pops the card into existence.
   */
  turn?: boolean | undefined;
}

/**
 * How long a thrown card is in the air.
 *
 * Here rather than only in the stylesheet because three things need it: the
 * animation, the moment the destination may stop hiding the card, and the
 * sweep that removes the element. The stylesheet reads it from a custom
 * property so there is one number rather than four kept equal by hand.
 */
const THROW_MS = 700;

/** Long enough after landing that the element is gone before it matters. */
const SWEEP_MS = THROW_MS + 100;

/** Middle of an element, in viewport pixels. */
function centreOf(element: Element | null): { x: number; y: number } | null {
  if (!element) return null;
  const box = element.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return null;
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
}

function zoneCentre(root: Element, zone: string) {
  return centreOf(root.querySelector(`[data-zone="${CSS.escape(zone)}"]`));
}

/**
 * Work out the flight a move should make, or null if it should not make one.
 *
 * Driven by the layout's `motions`, exactly as the table's animation is, so a
 * game that declares no motion for a move simply does not animate here either.
 * Only moves that send a card somewhere are animated: a motion whose
 * destination is the acting seat is a card arriving, and the arrival is
 * already visible - it appears in your hand.
 */
export function throwFor(
  layout: GameLayoutWire,
  move: string,
  card: string | undefined,
  root: Element | null,
): Omit<Throw, 'key'> | null {
  if (!card || !root) return null;

  const motion = (layout.motions ?? []).find((candidate) => candidate.move === move);
  if (!motion || motion.to === SEAT_END) return null;

  const from = centreOf(root.querySelector(`.hand-zones [data-card="${CSS.escape(card)}"]`));
  const to = zoneCentre(root, motion.to);
  if (!from || !to) return null;

  return { card, fromX: from.x, fromY: from.y, toX: to.x, toY: to.y };
}

/**
 * The card turned from one pile onto another, and the pile that must wait.
 *
 * Returns the flight to draw and the card the destination has to hold back
 * until it lands - without the second half the waste shows the card the
 * instant the packet arrives, and the animation becomes a back chasing a face
 * that is already home.
 *
 * Only motions with a zone at BOTH ends. A play out of your hand is `throwFor`
 * above, which has a card to fly from the moment you tap it; a draw into your
 * hand ends at a seat and is the draw slot's business.
 */
export function useTurned(
  layout: GameLayoutWire,
  move: { readonly n: number; readonly seat: number; readonly move: string } | null | undefined,
  zones: Readonly<Record<string, ZoneView>> | undefined,
  root: () => Element | null,
): { throws: readonly Throw[]; arriving: string | null; beneath: string | undefined } {
  const [throws, setThrows] = useState<Throw[]>([]);
  const [arriving, setArriving] = useState<string | null>(null);
  /*
   * What the destination was showing before this card set off.
   *
   * A pile the server trims to its top card has nothing left underneath once
   * that card is held back, so it fell through to drawing its own back - and
   * Golf's waste, which always has a card on it, appeared to turn face down
   * for the length of the flight and then show a new card. The pile should go
   * on showing what it was showing until the new card covers it.
   */
  const [beneath, setBeneath] = useState<string | undefined>(undefined);

  /* The piles as they were BEFORE this packet. Kept by the effect below, which
     runs after the counter event - so the difference between the two is what
     just moved. Same ordering `useArrival` and `useFlights` depend on. */
  const previous = useRef(zones);

  // Rebuilt every render; naming them as deps would re-run the effect and
  // cancel its timers.
  const latest = useRef({ layout, move, zones, root });
  latest.current = { layout, move, zones, root };

  const timers = useRef<number[]>([]);
  const after = useCallback((ms: number, run: () => void) => {
    timers.current.push(window.setTimeout(run, ms));
  }, []);
  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
    },
    [],
  );

  useCounterEvent(move?.n ?? 0, () => {
    const at = latest.current;
    const host = at.root();
    if (!at.move || !host) return;

    const motion = (at.layout.motions ?? []).find((c) => c.move === at.move?.move);
    if (!motion || motion.from === SEAT_END || motion.to === SEAT_END) return;

    const from = zoneCentre(host, motion.from);
    const to = zoneCentre(host, motion.to);
    if (!from || !to) return;

    /*
     * Which card landed, which is not necessarily one we may draw.
     *
     * Turning the waste back under the stock moves a card into a pile nobody
     * can see, so there is nothing to hold back and nothing to reveal - the
     * flight is a back going the other way and that is the whole of it.
     */
    const landed = landedCard(
      previous.current?.[motion.to]?.cards,
      at.zones?.[motion.to]?.cards,
    );

    const key = at.move.n;
    const flight: Throw = {
      key,
      card: landed ?? '',
      fromX: from.x,
      fromY: from.y,
      toX: to.x,
      toY: to.y,
      /* Face down off a hidden pile, and face down when the game says so: the
         turn is worth watching because you cannot yet see what it turned. */
      faceDown: motion.faceDown === true || landed === undefined,
      turn: true,
    };
    setThrows((current) => [...current, flight]);

    if (landed !== undefined) {
      const had = previous.current?.[motion.to]?.cards;
      setArriving(landed);
      setBeneath(had?.[had.length - 1]);
      // Cleared when the card LANDS rather than when the element is swept up,
      // or the pile sits empty for the extra beat after it has visibly arrived.
      after(THROW_MS, () => {
        setArriving((current) => (current === landed ? null : current));
      });
    }

    /* Swept on a timer rather than on animationend: a backgrounded tab may
       never fire the event, and a card stuck mid-screen is worse than a
       missed animation. */
    after(SWEEP_MS, () => {
      setThrows((current) => current.filter((candidate) => candidate.key !== key));
    });
  });

  useEffect(() => {
    previous.current = zones;
  }, [zones]);

  return { throws, arriving, beneath };
}

export function ThrownCards({ throws }: { throws: readonly Throw[] }) {
  return (
    <>
      {throws.map((flight) => (
        <span
          key={flight.key}
          className={flight.turn === true ? 'card-throw turning' : 'card-throw'}
          aria-hidden="true"
          style={
            {
              '--x0': `${String(flight.fromX)}px`,
              '--y0': `${String(flight.fromY)}px`,
              '--x1': `${String(flight.toX)}px`,
              '--y1': `${String(flight.toY)}px`,
              // The one place the throw's length is written; the reveal has to
              // wait for the card to land, so it cannot live only in the CSS.
              '--throw-ms': `${String(THROW_MS)}ms`,
            } as React.CSSProperties
          }
        >
          {flight.faceDown === true ? <CardBack /> : <Card card={flight.card} />}
        </span>
      ))}
    </>
  );
}
